import { Button } from "@/src/components/ui/Button";
import { Text } from "@/src/components/ui/Typography";
import { cn } from "@/src/lib/utils";

import type { ChatLauncherActionData } from "./data";

interface ChatLauncherActionProps {
  action: ChatLauncherActionData;
  visible: boolean;
  delayMs: number;
}

// Below `md` (touch, no hover) the label is always shown. From `md` up it is
// circular icon-only at rest; on hover the label reveals via a
// grid-template-columns 0fr -> 1fr transition (same technique
// FaqAccordionItem uses for its answer reveal) rather than animating
// `width`, since the target width depends on the label's own text length.
export function ChatLauncherAction({ action, visible, delayMs }: ChatLauncherActionProps) {
  const Icon = action.icon;

  return (
    <Button
      href={action.href}
      target="_blank"
      rel="noopener noreferrer"
      unstyled
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      className={cn(
        "group flex h-14 mr-6 items-center rounded-(--radius-full) bg-neutral-700 shadow-[0_10px_30px_rgba(32,34,35,0.25)] transition-[transform,opacity] duration-200 ease-out",
        visible
          ? "translate-y-0 scale-100 opacity-100"
          : "pointer-events-none translate-y-3 scale-90 opacity-0",
      )}
      style={{ transitionDelay: visible ? `${delayMs}ms` : "0ms" }}
    >
      <span className="flex h-14 w-14 shrink-0 items-center justify-center">
        <Icon size={22} weight="regular" className="text-white" />
      </span>

      <span className="grid grid-cols-[1fr] transition-[grid-template-columns] duration-500 ease-out md:grid-cols-[0fr] md:group-hover:grid-cols-[1fr]">
        <span className="overflow-hidden">
          <Text as="span" size="bodySMMedium" className="block whitespace-nowrap pr-6 text-white">
            {action.label}
          </Text>
        </span>
      </span>
    </Button>
  );
}
